import { parseISO, format, formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import Loading from '../../components/common/loading';
import ReactPaginate from 'react-paginate';
import React from 'react';
import './activityLog.css';
import {
  AppToolbar,
  AppToolbarField,
  AppToolbarGrid,
} from '../../components/common/appShell';

function parseQuery(queryString) {
  const query = {};
  const pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i].split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
}

function getActivityFilterFromLocation() {
  if (typeof window === 'undefined') {
    return '';
  }

  return parseQuery(window.location.search).action || '';
}

const activityIcons = {
  user_edited: { icon: 'manage_accounts', url: '/users/logged-in' },
  pat_created: { icon: 'key', url: '' },
  pat_deleted: { icon: 'delete', url: '' },
  guest_user_created: { icon: 'group_add', url: '' },
  guest_user_modified: { icon: 'edit', url: '' },
  guest_user_deleted: { icon: 'delete', url: '' },

  device_paired: { icon: 'login', url: '/devices/connected/:resourceId/overview' },
  device_unpaired: { icon: 'logout', url: '/devices/connected/:resourceId/overview' },
  device_disconnected: { icon: 'cloud_off', url: '/devices/connected/:resourceId/overview' },
  device_connected: { icon: 'cloud_done', url: '/devices/connected/:resourceId/overview' },
  device_edited: { icon: 'edit', url: '/devices/connected/:resourceId/configuration/device' },
  device_firmware_updated: { icon: 'update', url: '/devices/connected/:resourceId/configuration/firmware' },

  outlet_switched: { icon: "outlet", url: "/devices/connected/:enclosureSn/outlets/:resourceId", },
  outlet_edited: { icon: "edit", url: "/devices/connected/:enclosureSn/outlets/:resourceId", },
  device_fw_update: { icon: 'update', url: '/devices/connected/:resourceId/configuration/firmware' },

  event_triggered: { icon: 'notification', url: '' },
  event_created: { icon: 'event_upcoming', url: '' },
  event_edited: { icon: 'edit_calendar', url: '' },

  group_created: { icon: 'ballot', url: '' },
  group_edited: { icon: 'edit', url: '' },
  group_deleted: { icon: 'delete', url: '' },
};

const ACTIVITY_TYPE_OPTIONS = [
  { value: '', label: 'All activity' },
  { value: 'device_paired,device_unpaired', label: 'Device pairing' },
  { value: 'device_disconnected,device_connected', label: 'Device connection' },
  { value: 'device_edited', label: 'Device editing' },
  { value: 'outlet_switched,outlet_edited', label: 'Outlet switching and editing' },
  { value: 'device_fw_update', label: 'Firmware updates' },
  {
    value: 'group_created,group_edited,group_deleted',
    label: 'Device group changes',
    hideForSelectedDevice: true,
  },
];

export default class ActivityLog extends React.Component {
  state = {
    activityFilter: this.props.initialActivityFilter || getActivityFilterFromLocation(),
    searchText: '',
    appliedSearch: '',
    dark: document.body.classList.contains('dark-mode'),
  };

  componentDidMount() {
    this.darkObserver = new MutationObserver(() => {
      this.setState({ dark: document.body.classList.contains('dark-mode') });
    });
    this.darkObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.initialActivityFilter !== this.props.initialActivityFilter &&
      this.props.initialActivityFilter !== this.state.activityFilter
    ) {
      this.setState({ activityFilter: this.props.initialActivityFilter || '' });
    }
  }

  componentWillUnmount() {
    this.darkObserver && this.darkObserver.disconnect();
  }

  getActivityTypeOptions() {
    return ACTIVITY_TYPE_OPTIONS.filter(
      (option) => !(option.hideForSelectedDevice && this.props.selectedDevice)
    );
  }

  getActivityTypeLabel(value) {
    return this.getActivityTypeOptions().find((option) => option.value === value)?.label || value;
  }

  applySearch = () => {
    const nextSearch = this.state.searchText;
    this.setState({ appliedSearch: nextSearch });
    this.props.searchActivity && this.props.searchActivity(nextSearch);
  };

  handleActivityFilterChange = (val) => {
    this.setState({ activityFilter: val });
    this.props.onFilter && this.props.onFilter(val);
  };

  clearFilters = () => {
    this.setState({ activityFilter: '', searchText: '', appliedSearch: '' });
    if (this.props.onClearFilters) {
      this.props.onClearFilters();
      return;
    }
    this.props.searchActivity && this.props.searchActivity('');
    this.props.onFilter && this.props.onFilter('');
  };

  render() {
    const {
      activity = [],
      refresh,
      title,
      loading,
      searchActivity,
      onExportCsv,
      exportingCsv,
      meta = {},
      page = 1,
      onPageChange,
    } = this.props;

    const activityTypeOptions = this.getActivityTypeOptions();
    const hasSearchFilter = Boolean(this.state.appliedSearch);
    const hasTypeFilter = Boolean(this.state.activityFilter);
    const hasActiveFilters = hasSearchFilter || hasTypeFilter;
    const totalCount = typeof meta?.totalCount === 'number' ? meta.totalCount : undefined;
    const pageLimit = meta?.limit || activity.length || 1;
    let summaryText = null;
    if (typeof totalCount === 'number' && !(loading && activity.length === 0)) {
      if (totalCount === 0) {
        summaryText = 'Showing 0 of 0 activity entries';
      } else if (activity.length === 0) {
        summaryText = `Showing 0 of ${totalCount} activity entries`;
      } else {
        const showingStart = (page - 1) * pageLimit + 1;
        const showingEnd = Math.min(showingStart + activity.length - 1, totalCount);
        summaryText = `Showing ${showingStart}-${showingEnd} of ${totalCount} activity entries`;
      }
    }
    return (
      <div
        className="activity-log"
      >
        {title && (
          <h4 className="activity-log__title">{title}</h4>
        )}
        <AppToolbar variant="plain" className="activity-log__toolbar">
          <AppToolbarGrid wide withActions className="activity-log__toolbar-grid">
            {searchActivity && (
              <AppToolbarField label="Search activity" grow>
                <div className="app-toolbar__search">
                  <input
                    type="text"
                    placeholder="Search by serial number or device name"
                    value={this.state.searchText}
                    onChange={(e) => this.setState({ searchText: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        this.applySearch();
                      }
                    }}
                  />
                  <span
                    className="search-icon-button"
                    onClick={this.applySearch}
                  >
                    <i className="material-icons">search</i>
                  </span>
                </div>
              </AppToolbarField>
            )}
            <AppToolbarField label="Activity type">
              <select
                onChange={(e) => {
                  this.handleActivityFilterChange(e.target.value);
                }}
                value={this.state.activityFilter}
                aria-label="Activity type"
              >
                {activityTypeOptions.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </AppToolbarField>
            <AppToolbarField label="Refresh">
              {loading ? (
                <div className="activity-log__loading-action">
                  <Loading black />
                </div>
              ) : refresh ? (
                <button
                  type="button"
                  className="button secondary app-toolbar__action-button button--icon-label activity-log__action-button"
                  onClick={() => refresh()}
                >
                  <i className="material-icons" aria-hidden="true">
                    refresh
                  </i>
                  <span>Refresh</span>
                </button>
              ) : null}
            </AppToolbarField>
            {onExportCsv && (
              <AppToolbarField label="Export">
                <button
                  type="button"
                  className="button secondary app-toolbar__action-button button--icon-label activity-log__action-button"
                  onClick={onExportCsv}
                  disabled={exportingCsv || loading}
                >
                  <i className="material-icons" aria-hidden="true">
                    download
                  </i>
                  <span>{exportingCsv ? 'Exporting...' : 'Export CSV'}</span>
                </button>
              </AppToolbarField>
            )}
            {hasActiveFilters && (
              <AppToolbarField label="Actions">
                <button
                  type="button"
                  className="button secondary app-toolbar__action-button activity-log__action-button"
                  onClick={this.clearFilters}
                >
                  Clear filters
                </button>
              </AppToolbarField>
            )}
          </AppToolbarGrid>
          {hasActiveFilters && (
            <div className="activity-log__active-filters">
              {hasSearchFilter && (
                <span className="activity-log__filter-pill">
                  Search: {this.state.appliedSearch}
                </span>
              )}
              {hasTypeFilter && (
                <span className="activity-log__filter-pill">
                  Type: {this.getActivityTypeLabel(this.state.activityFilter)}
                </span>
              )}
            </div>
          )}
        </AppToolbar>
        {summaryText && (
          <div className="activity-log__summary">{summaryText}</div>
        )}

        <div className="activity-log__list">
          {activity.length > 0 ? (
            <div>
              {activity.map((history) => {
                const timestamp = parseISO(history.createdAt);
                return (
                  <div key={history.createdAt} className="activity-log__entry">
                    <div className="activity-log__entry-main">
                      <span className="activity-log__entry-icon">
                        <i className="material-icons" aria-hidden="true">
                          {activityIcons[history.action]
                            ? activityIcons[history.action].icon
                            : 'edit'}
                        </i>
                      </span>
                      <div className="activity-log__entry-content">
                        <div className="activity-log__entry-description">
                          {history.description}
                          {activityIcons[history.action] && activityIcons[history.action].url ? (
                            <>
                              <span className="activity-log__entry-separator">:</span>
                              <span className="activity-log__entry-link">
                                <Link
                                  to={activityIcons[history.action].url.replace(
                                    ':resourceId',
                                    history.resourceId
                                  ).replace(':enclosureSn', history.enclosureSn)}
                                >
                                  {history.resourceName}
                                </Link>
                              </span>
                            </>
                          ) : (
                            `: ${history.resourceName}`
                          )}
                          {!history.details && history.counter > 1 &&
                            ` ${history.counter} times within 5 minutes`}
                        </div>
                        <div className="activity-log__entry-time">
                          {format(timestamp, 'MMM d, HH:mm ')} -
                          {` ${formatDistanceToNow(timestamp)} ago`}
                        </div>
                      </div>
                    </div>
                    <div className="activity-log__entry-detail-wrap">
                      {history.details && (
                        <div className="activity-log__entry-detail">
                          {history.details}{' '}
                          {history.counter > 1 &&
                            `${history.counter} times within 5 minutes`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {meta.totalPages > 1 && onPageChange && (
                <ReactPaginate
                  pageCount={meta.totalPages || 1}
                  forcePage={(page || 1) - 1}
                  onPageChange={({ selected }) => onPageChange(selected + 1)}
                  pageClassName="page-item"
                  previousLabel="Previous"
                  nextLabel="Next"
                  containerClassName="react-paginate"
                  activeClassName="selected"
                />
              )}
            </div>
          ) : loading ? (
            <Loading black />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              No Activity Yet
            </div>
          )}
        </div>
      </div>
    );
  }
}
